import React from "react";
import type { Appointment } from "@/lib/api";
import { statusMeta, WEEKDAYS, startOfWeek, addDays, sameDay, fmtTime } from "./constants";

function AppointmentChip({ a, onOpen }: { a: Appointment; onOpen: (id: string) => void }) {
  const m = statusMeta(a.status);
  return (
    <button
      onClick={() => onOpen(a.id)}
      data-testid={`appt-chip-${a.id}`}
      className={`w-full rounded-md border border-l-4 ${m.bar} border-slate-200 bg-white px-2 py-1.5 text-left transition-all hover:border-blue-300 hover:shadow-sm`}
    >
      <p className="truncate text-xs font-semibold text-slate-900">{fmtTime(a.start_at)} · {a.contact_name ?? "—"}</p>
      <p className="truncate text-[11px] text-slate-500">{a.service_name ?? a.requested_service ?? "Appointment"}</p>
    </button>
  );
}

function DayColumn({ date, appts, onOpen, isToday, onAddAt }: {
  date: Date; appts: Appointment[]; onOpen: (id: string) => void; isToday: boolean; onAddAt: (d: Date) => void;
}) {
  const dayAppts = appts.filter((a) => sameDay(new Date(a.start_at), date)).sort((x, y) => x.start_at.localeCompare(y.start_at));
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-testid={`day-col-${date.toISOString().slice(0, 10)}`}>
      <div className={`mb-2 flex items-center justify-between rounded-md px-2 py-1.5 ${isToday ? "bg-blue-50" : ""}`}>
        <div>
          <p className="text-[11px] font-medium uppercase text-slate-400">{WEEKDAYS[date.getDay()]}</p>
          <p className={`text-sm font-semibold ${isToday ? "text-blue-700" : "text-slate-800"}`}>{date.getDate()}</p>
        </div>
        <button onClick={() => onAddAt(date)} data-testid={`add-on-${date.toISOString().slice(0, 10)}`}
          className="rounded-md px-1.5 text-lg leading-none text-slate-300 transition-colors hover:text-blue-600">+</button>
      </div>
      <div className="space-y-1.5">
        {dayAppts.length === 0 ? <p className="px-1 py-2 text-[11px] text-slate-300">—</p>
          : dayAppts.map((a) => <AppointmentChip key={a.id} a={a} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

export function WeekView({ anchor, appts, onOpen, onAddAt }: {
  anchor: Date; appts: Appointment[]; onOpen: (id: string) => void; onAddAt: (d: Date) => void;
}) {
  const start = startOfWeek(anchor);
  const today = new Date();
  return (
    <div className="grid grid-cols-7 gap-2 overflow-x-auto" data-testid="week-view">
      {Array.from({ length: 7 }).map((_, i) => {
        const d = addDays(start, i);
        return <DayColumn key={i} date={d} appts={appts} onOpen={onOpen} isToday={sameDay(d, today)} onAddAt={onAddAt} />;
      })}
    </div>
  );
}

export function DayView({ anchor, appts, onOpen, onAddAt }: {
  anchor: Date; appts: Appointment[]; onOpen: (id: string) => void; onAddAt: (d: Date) => void;
}) {
  const today = new Date();
  return (
    <div className="mx-auto max-w-2xl" data-testid="day-view">
      <DayColumn date={anchor} appts={appts} onOpen={onOpen} isToday={sameDay(anchor, today)} onAddAt={onAddAt} />
    </div>
  );
}

export function MonthView({ anchor, appts, onOpen, onDayClick }: {
  anchor: Date; appts: Appointment[]; onOpen: (id: string) => void; onDayClick: (d: Date) => void;
}) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const today = new Date();
  const cells = Array.from({ length: 42 }).map((_, i) => addDays(gridStart, i));
  return (
    <div data-testid="month-view">
      <div className="mb-1 grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w) => <div key={w} className="px-2 py-1 text-center text-[11px] font-medium uppercase text-slate-400">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === anchor.getMonth();
          const dayAppts = appts.filter((a) => sameDay(new Date(a.start_at), d)).sort((x, y) => x.start_at.localeCompare(y.start_at));
          return (
            <div key={i} onClick={() => onDayClick(d)} data-testid={`month-cell-${d.toISOString().slice(0, 10)}`}
              className={`min-h-[92px] cursor-pointer rounded-md border p-1.5 transition-colors ${inMonth ? "border-slate-200 bg-white hover:border-blue-300" : "border-transparent bg-slate-50/50"}`}>
              <p className={`mb-1 text-xs font-semibold ${sameDay(d, today) ? "text-blue-700" : inMonth ? "text-slate-700" : "text-slate-300"}`}>{d.getDate()}</p>
              <div className="space-y-0.5">
                {dayAppts.slice(0, 3).map((a) => {
                  const m = statusMeta(a.status);
                  return (
                    <button key={a.id} onClick={(e) => { e.stopPropagation(); onOpen(a.id); }} data-testid={`month-appt-${a.id}`}
                      className={`block w-full truncate rounded border-l-2 ${m.bar} bg-slate-50 px-1 py-0.5 text-left text-[10px] text-slate-600 hover:bg-slate-100`}>
                      {fmtTime(a.start_at)} {a.contact_name ?? ""}
                    </button>
                  );
                })}
                {dayAppts.length > 3 && <p className="px-1 text-[10px] text-slate-400">+{dayAppts.length - 3} more</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
